/* Weight goal (profile.weightGoal = { weight, date, startWeight?, startDate? }):
 * where Mo stands against a target weight by a date, the pace that needs, the
 * pace he is on (the 28-day weigh-in trend) and roughly what the gap means in
 * kcal a day. Twin of src/fitlogic/goal.py, pinned by vectors/goal.json. */
import { avgWeightOn } from './measurements';
import { weightSlope } from './trends';

export const KCAL_PER_KG = 7700;
const TREND_DAYS = 28;
const MIN_TREND_SPAN = 10; // days between the first and last weigh-in before the trend counts
const MAX_KCAL_CHANGE = 500; // a bigger jump in a day's target is rarely sensible
const AGGRESSIVE_PCT = 1; // % of bodyweight a week; faster cuts cost muscle
const CLOSE = 0.8; // at least 80% of the needed pace

const DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const isNum = v => typeof v === 'number' && Number.isFinite(v);
const isObj = v => !!v && typeof v === 'object' && !Array.isArray(v);
/* Half-up like the Python twin's floor(x * f + 0.5) / f; `|| 0` drops -0. */
const roundF = (x, f) => Math.round(x * f) / f || 0;
const round1 = x => roundF(x, 10);
const round2 = x => roundF(x, 100);
const roundTo = (x, step) => Math.round(x / step) * step || 0;

function dayNumber(key) {
  if (typeof key !== 'string' || !DATE_RE.test(key)) return null;
  const [y, m, d] = key.split('-').map(Number);
  const back = new Date(Date.UTC(y, m - 1, d));
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
  return Math.round(back.getTime() / 86400000);
}
const keyOf = n => new Date(n * 86400000).toISOString().slice(0, 10);

function latestWeight(metrics, today) {
  let best = null;
  for (const m of metrics) {
    if (!isObj(m) || dayNumber(m.date) === null || !isNum(m.weight) || m.weight <= 0 || m.date > today) continue;
    if (!best || m.date > best.date) best = m;
  }
  return best ? best.weight : null;
}

/**
 * Progress towards profile.weightGoal on `today`.
 * @param {object} state reads profile.weightGoal, bodyMetrics
 * @param {string} today YYYY-MM-DD
 * @param {{tdee?: number}} opts tdee: maintenance kcal (energy estimateEnergyBalance) for suggestedKcal
 * @returns {null | {target, date, startWeight, startDate, current, daysLeft, weeksLeft, direction,
 *   toGo, neededPerWeek, neededPctPerWeek, trendPerWeek, trendSpanDays, projectedOnDate,
 *   projectedDate, status, aggressive, progress, kcalChange, suggestedKcal}}
 *   null without a valid goal. current = 7-day average weight (else the latest weigh-in).
 *   direction 'lose' | 'gain' (from the start weight, else current). Per-week values are kg,
 *   negative = losing. status: 'no-data' (no weigh-in), 'reached', 'past' (date passed),
 *   'no-trend' (under 2 weigh-ins or 10 days of them in the last 28), 'on-track' (trend at least
 *   the needed pace), 'close' (80%+), 'behind'. kcalChange: kcal a day to add (negative: eat
 *   less) to move from the trend to the needed pace, rounded to 10, at most 500 either way.
 *   suggestedKcal: tdee + the needed pace in kcal, rounded to 50 (null without tdee).
 *   projectedDate: when the trend reaches the target (null when it moves away or barely moves).
 */
export function weightGoalProgress(state, today, opts = {}) {
  const st = isObj(state) ? state : {};
  const goal = isObj(st.profile) ? st.profile.weightGoal : null;
  const t = dayNumber(today);
  const end = isObj(goal) ? dayNumber(goal.date) : null;
  if (!isObj(goal) || !isNum(goal.weight) || goal.weight <= 0 || end === null || t === null) return null;
  const metrics = Array.isArray(st.bodyMetrics) ? st.bodyMetrics : [];
  const avg = avgWeightOn(metrics, today);
  const current = avg !== null ? avg : latestWeight(metrics, today);
  const startWeight = isNum(goal.startWeight) && goal.startWeight > 0 ? goal.startWeight : null;
  const startDate = dayNumber(goal.startDate) !== null ? goal.startDate : null;
  const daysLeft = end - t;
  const out = {
    target: goal.weight,
    date: goal.date,
    startWeight,
    startDate,
    current,
    daysLeft,
    weeksLeft: round1(daysLeft / 7),
    direction: goal.weight < (startWeight !== null ? startWeight : (current !== null ? current : goal.weight)) ? 'lose' : 'gain',
    toGo: null,
    neededPerWeek: null,
    neededPctPerWeek: null,
    trendPerWeek: null,
    trendSpanDays: null,
    projectedOnDate: null,
    projectedDate: null,
    status: 'no-data',
    aggressive: false,
    progress: null,
    kcalChange: null,
    suggestedKcal: null,
  };
  if (current === null) return out;
  const lose = out.direction === 'lose';
  out.toGo = round2(current - goal.weight);
  if (startWeight !== null && startWeight !== goal.weight) {
    out.progress = round2(Math.min(1, Math.max(0, (startWeight - current) / (startWeight - goal.weight))));
  }
  const slope = weightSlope(metrics, today, TREND_DAYS);
  if (slope && slope.spanDays >= MIN_TREND_SPAN) {
    out.trendPerWeek = slope.kgPerWeek;
    out.trendSpanDays = slope.spanDays;
  }
  if (lose ? current <= goal.weight : current >= goal.weight) {
    out.status = 'reached';
    return out;
  }
  if (daysLeft <= 0) {
    out.status = 'past';
    return out;
  }
  const needed = (goal.weight - current) / (daysLeft / 7);
  out.neededPerWeek = round2(needed);
  out.neededPctPerWeek = round2((needed / current) * 100);
  out.aggressive = Math.abs(needed / current) * 100 > AGGRESSIVE_PCT;
  if (isNum(opts.tdee)) out.suggestedKcal = roundTo(opts.tdee + (needed * KCAL_PER_KG) / 7, 50);
  if (out.trendPerWeek === null) {
    out.status = 'no-trend';
    return out;
  }
  const trend = out.trendPerWeek;
  out.projectedOnDate = round2(current + (trend * daysLeft) / 7);
  // Only a trend that moves towards the target (by 50 g a week or more) gets a date.
  if ((lose ? trend <= -0.05 : trend >= 0.05)) {
    out.projectedDate = keyOf(t + Math.ceil(((goal.weight - current) / trend) * 7));
  }
  const ratio = trend / needed;
  out.status = ratio >= 1 ? 'on-track' : ratio >= CLOSE ? 'close' : 'behind';
  const change = ((needed - trend) * KCAL_PER_KG) / 7;
  out.kcalChange = roundTo(Math.max(-MAX_KCAL_CHANGE, Math.min(MAX_KCAL_CHANGE, change)), 10);
  return out;
}
